<template>
  <div class="mt-5 card" data-testid="partner-gallery">
    <h2 class="mb-6 text-headline-m">{{ t('PartnerCompany.gallery.headline') }}</h2>

    <FileDropper
      class="mb-6"
      accept="image/jpeg,image/png,image/webp,image/gif"
      :image-dimensions-hint="t('PartnerCompany.gallery.imageDimensionsHint')"
      :file-formats-hint="t('PartnerCompany.gallery.fileFormatsHint')"
      @input="onFilesPicked"
    />

    <MessageBanner
      v-if="tooManyImages"
      type="error"
      :message="t('PartnerCompany.gallery.imageCountError', { maxImageCount })"
      data-testid="partner-gallery-error"
    />

    <div class="mt-4 flex flex-wrap gap-3">
      <div v-for="image in model.images" :key="imageKey(image)" class="group relative">
        <GalleryImage :image="image" class="h-32 w-48 rounded-sm object-cover group-hover:brightness-50" />
        <button
          class="absolute top-0 left-0 hidden h-full w-full items-center justify-center text-center text-inverse group-hover:flex"
          data-testid="partner-gallery-remove"
          @click="onRemoveImage(image)"
        >
          <MdiSvg :path="mdiTrashCanOutline" size="20" />
        </button>
      </div>
    </div>

    <TextInput
      id="videoUrl"
      v-model="model.videoUrl"
      name="videoUrl"
      type="url"
      class="mt-6"
      :label="t('PartnerCompany.gallery.videoUrl')"
      :error="getValidationErrors(props.v$.gallery.videoUrl)"
      data-testid="partner-gallery-video"
    />
  </div>
</template>

<script setup lang="ts">
import { mdiTrashCanOutline } from '@mdi/js'
import { MdiSvg, MessageBanner } from '@ramp106/omrjs-core-ui'
import { TextInput } from '@ramp106/omrjs-core-ui/deprecated'
import type { Validation } from '@vuelidate/core'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import type { AttachmentInput, Attachment } from '@/gql/myomr'
import { getValidationErrors } from '@/helpers/validationHelper'

import FileDropper from './FileDropper.vue'
import GalleryImage from './GalleryImage.vue'

const { t } = useI18n()

const props = defineProps<{
  v$: Validation
}>()

type ModelType = {
  videoUrl: string
  images: (AttachmentInput | Attachment)[]
}

const model = defineModel<ModelType>({
  default: () => ({
    videoUrl: '',
    images: [],
  }),
})

const imageKey = (image: ModelType['images'][number]) => {
  if ('id' in image) return image.id
  if ('file' in image) return (image.file as File)?.name
}

const maxImageCount = 8
const tooManyImages = computed(() => model.value.images.length > maxImageCount)

function onRemoveImage(image: ModelType['images'][number]) {
  model.value.images = model.value.images.filter((img) => img !== image)
}

function onFilesPicked(fileList: FileList) {
  if (!fileList.length) return
  const fileInputs: AttachmentInput[] = []
  for (const file of fileList) {
    fileInputs.push({ file })
  }
  model.value.images = [...model.value.images, ...fileInputs]
}
</script>
